import type { ZodIssue } from 'zod'
import { isJsonObject, type Json, type JsonObject } from '@lib/helpers/json'

/**
 * A place in the document: `['pricing', 'high', 'rows', 0, 2]`.
 *
 * Numbers index lists, strings index objects — the same shape Zod reports an
 * issue at, which is what lets a complaint from the schema be shown under the
 * field that caused it without a lookup table mapping one to the other.
 */
export type Path = readonly (string | number)[]

/**
 * The document a form is holding, which is **not** a `VenueMarketingContent`.
 *
 * It cannot be: a half-filled form does not satisfy the contract, and typing it
 * as though it did would mean asserting a shape that is not there yet — which is
 * both a lie and something `cse/no-unsafe-type-assertion` rightly refuses. So
 * the draft is honest JSON, and becomes a `VenueMarketingContent` at exactly one
 * moment: when the save schema parses it.
 */
export type Draft = JsonObject

/** Read whatever is at a path, or undefined if nothing is. */
export function getAt(draft: Json, path: Path): Json | undefined {
  let current: Json | undefined = draft
  for (const step of path) {
    if (current === undefined || current === null) return undefined
    if (typeof step === 'number') {
      if (!Array.isArray(current)) return undefined
      current = current[step]
    } else {
      if (!isJsonObject(current)) return undefined
      current = current[step]
    }
  }
  return current
}

/** The string at a path, or '' — what a text input needs to stay controlled. */
export function getString(draft: Json, path: Path): string {
  const value = getAt(draft, path)
  return typeof value === 'string' ? value : ''
}

/** The list at a path, or an empty one. */
export function getList(draft: Json, path: Path): Json[] {
  const value = getAt(draft, path)
  return Array.isArray(value) ? value : []
}

/**
 * A copy of the draft with `value` at `path`, creating containers on the way.
 *
 * Immutable, because React state is. Missing containers are created to match
 * the next step — a number makes a list, a string makes an object — so a field
 * deep inside a section that does not exist yet can still be typed into, which
 * is the normal case for a venue filling one in from scratch.
 *
 * Passing `undefined` removes the key or list entry instead of storing a hole:
 * an absent optional section must be genuinely absent, since the save schema is
 * strict and `{ lodging: undefined }` is not the same as no lodging at all.
 */
export function setAt(draft: Draft, path: Path, value: Json | undefined): Draft {
  if (path.length === 0) return draft
  const next = setIn(draft, path, value, 0)
  return isJsonObject(next) ? next : draft
}

function setIn(current: Json | undefined, path: Path, value: Json | undefined, depth: number): Json {
  const step = path[depth]
  if (step === undefined) return value ?? null
  const last = depth === path.length - 1

  if (typeof step === 'number') {
    const list = Array.isArray(current) ? [...current] : []
    if (last) {
      if (value === undefined) {
        list.splice(step, 1)
        return list
      }
      list[step] = value
      return list
    }
    list[step] = setIn(list[step], path, value, depth + 1)
    return list
  }

  const object: JsonObject = isJsonObject(current) ? { ...current } : {}
  if (last) {
    if (value === undefined) {
      // Deleted rather than set to null: the save schema is strict, and a null
      // where a section should be reads as "present and wrong" rather than absent.
      delete object[step]
      return object
    }
    object[step] = value
    return object
  }
  object[step] = setIn(object[step], path, value, depth + 1)
  return object
}

/** Append to the list at a path. */
export function appendAt(draft: Draft, path: Path, value: Json): Draft {
  return setAt(draft, [...path, getList(draft, path).length], value)
}

/** Remove one entry from the list at a path. */
export function removeAt(draft: Draft, path: Path, index: number): Draft {
  return setAt(draft, [...path, index], undefined)
}

/** Move the entry at `index` by `delta`, clamped; the draft is returned unchanged if it cannot move. */
export function moveAt(draft: Draft, path: Path, index: number, delta: number): Draft {
  const list = getList(draft, path)
  const target = index + delta
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return draft
  const reordered = [...list]
  const [moved] = reordered.splice(index, 1)
  if (moved === undefined) return draft
  reordered.splice(target, 0, moved)
  return setAt(draft, path, reordered)
}

/** A path as the key used to look its messages up: `pricing.high.rows.0`. */
export function pathKey(path: Path): string {
  return path.join('.')
}

/**
 * Every complaint the schema made, keyed by the path it was made about.
 *
 * One key per path with all of its messages, so a field showing its own error
 * needs no search. Issues are also recorded against each ancestor path under a
 * separate map, because a rule about a whole table ("row 1 has 3 cells but there
 * are 2 columns") is reported at the row and needs to be visible from the
 * section that owns it even when that row is not on screen.
 */
export interface DraftProblems {
  /** Messages for exactly this path. */
  byPath: Map<string, string[]>
  /** Messages for this path or anything under it, for section-level summaries. */
  bySubtree: Map<string, string[]>
}

export function problemsFrom(issues: readonly ZodIssue[]): DraftProblems {
  const byPath = new Map<string, string[]>()
  const bySubtree = new Map<string, string[]>()
  for (const issue of issues) {
    // Zod reports a path of `PropertyKey`, which admits symbols; a document
    // parsed from JSON has none, and narrowing here keeps `Path` honest rather
    // than widening it to a type no field could be rendered from.
    const path: Path = issue.path.map((step) =>
      typeof step === 'number' ? step : String(step),
    )
    const key = pathKey(path)
    byPath.set(key, [...(byPath.get(key) ?? []), issue.message])
    // Every prefix, so a section knows about a problem nested anywhere inside it.
    for (let depth = 0; depth <= path.length; depth += 1) {
      const prefix = pathKey(path.slice(0, depth))
      bySubtree.set(prefix, [...(bySubtree.get(prefix) ?? []), issue.message])
    }
  }
  return { byPath, bySubtree }
}

/** No complaints, for the state before a save has been attempted. */
export function noProblems(): DraftProblems {
  return { byPath: new Map(), bySubtree: new Map() }
}
